<script setup lang="ts">
import { storeToRefs } from 'pinia'

import AcceptedGenres from '~/components/influencer/profile/about/AcceptedGenres.vue'
import Conditions from '~/components/influencer/profile/about/Conditions.vue'
import Description from '~/components/influencer/profile/about/Description.vue'
import InfluencerProfileExpectations from '~/components/influencer/profile/about/Expectations.vue'
import Favorite from '~/components/influencer/profile/about/Favorite.vue'
import GenresList from '~/components/influencer/profile/about/GenresList.vue'
import Plan from '~/components/influencer/profile/about/Plan.vue'
import SeoLists from '~/components/influencer/profile/about/SeoLists.vue'
import HelpBlock from '~/components/ui/Cards/HelpBlock.vue'
import VCard from '~/components/ui/VCard.vue'
import VRecentOpportunities from '~/components/ui/VRecentOpportunities.vue'

import { useComputeMismatches } from '~/composables/useComputeMismatches'
import { useCuratorContestsInfos } from '~/composables/useCuratorContestsInfos'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { useUserStore } from '~/stores/user'

import { provideFetchRecentOpportunities } from '~/api-core/Curators/FetchRecentOpportunities'

import type {
  Influencer,
  InfluencerPreferences,
  InfluencerRecentOpportunities,
} from '~/types/influencer'

type Props = {
  genresConfiguration: InfluencerPreferences
  influencer: Influencer
}

type Slots = {
  'post-content'(): any
}

const props = defineProps<Props>()
defineSlots<Slots>()

const { coreFetch } = useProvideCoreFetch()

const { isFeatureOnForGroup } = useGrowthBook()
const { getInfluencerExpectationsData } = useComputeMismatches()
const { isContest } = useCuratorContestsInfos(props.influencer)
const { data: recentOpportunities } = useAsyncData<
  InfluencerRecentOpportunities[]
>(
  () => {
    const fetchRecentOpportunities = provideFetchRecentOpportunities(
      coreFetch,
      props.influencer.slug,
    )
    return fetchRecentOpportunities()
  },
  {
    default: () => [],
  },
)

const { IS_LOGGED_IN, IS_INF } = storeToRefs(useUserStore())

const influencerExpectationsData = getInfluencerExpectationsData(
  props.influencer,
)

const showRecentOpportunities = computed<boolean>(
  () => recentOpportunities.value.length > 0,
)
const genresAcceptedMostOftenIds = computed<number[]>(
  () => props.influencer.latest_stats?.genres_accepted_often ?? [],
)
</script>

<template>
  <div class="tw-flex tw-w-full tw-flex-col tw-gap-6">
    <VCard class="tw-w-full" padding="5">
      <Description :influencer="influencer" />
      <slot name="post-content" />
    </VCard>
    <AcceptedGenres :genre-ids="genresAcceptedMostOftenIds" />
    <GenresList
      v-for="([target, tagInformations], index) in Object.entries(
        genresConfiguration,
      )"
      :key="index"
      :target="target"
      :tag-informations="tagInformations"
    />
    <HelpBlock
      v-if="!IS_INF"
      :link="$t('influencer.profile.genresFAQ.link')"
      :title="$t('influencer.profile.genresFAQ.title')"
      :description="$t('influencer.profile.genresFAQ.subtitle')"
    />

    <Favorite :influencer="influencer" />
    <VRecentOpportunities
      v-if="showRecentOpportunities"
      :recent-opportunities="recentOpportunities"
      :influencer="influencer"
    />
    <Conditions v-if="isContest" :influencer="influencer" />
    <Plan v-if="isContest" :influencer="influencer" />
    <InfluencerProfileExpectations
      :expectations-data="influencerExpectationsData"
    />
    <SeoLists
      v-if="!IS_LOGGED_IN"
      class="tw-w-full"
      :seo-pages="influencer.seo_pages"
    />
  </div>
</template>
